import { api, unwrap } from "@lumberset/api-client";
import { date, money } from "@lumberset/takeoff-ui";
import {
  Badge,
  Button,
  Container,
  Drawer,
  EmptyState,
  Field,
  IconButton,
  Input,
  Prompt,
  SectionHeader,
  Select,
  Switch,
  Table,
  Textarea,
  toast,
} from "@lumberset/ui";
import { Plus, ReceiptPercent, SquareTwoStack } from "@medusajs/icons";
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { type FormEvent, useState } from "react";
import { Crumbs } from "../../components/crumbs";
import { centsToDollars, dollarsToCents, wholeNumber } from "../../lib/money";
import { couponRedemptionsQuery, couponsQuery } from "../../lib/queries";

export const Route = createFileRoute("/_app/coupons")({
  loader: ({ context: { queryClient } }) => queryClient.ensureQueryData(couponsQuery),
  component: Coupons,
});

type Coupon = Awaited<ReturnType<NonNullable<typeof couponsQuery.queryFn>>>["coupons"][number];
type Kind = Coupon["kind"];

const kindLabels: Record<Kind, string> = {
  percent_off: "Percent off",
  amount_off: "Amount off",
  free_quotes: "Credits",
};

/** "50% off, 2 uses", "$25.00 off", "3 credits". */
function worth(coupon: Coupon): string {
  switch (coupon.kind) {
    case "percent_off":
      return `${coupon.percentOff}% off${coupon.uses > 1 ? `, ${coupon.uses} uses` : ""}`;
    case "amount_off":
      return `${money(coupon.amountOffCents ?? 0)} off${coupon.uses > 1 ? `, ${coupon.uses} uses` : ""}`;
    case "free_quotes":
      return `${coupon.freeQuotes} ${coupon.freeQuotes === 1 ? "credit" : "credits"}`;
  }
}

const expired = (coupon: Coupon) =>
  coupon.expiresAt !== null && new Date(coupon.expiresAt).getTime() <= Date.now();

async function copyCode(code: string) {
  try {
    await navigator.clipboard.writeText(code);
    toast.success(`${code} copied`);
  } catch {
    toast.error("Could not copy. Select the code and copy it by hand.");
  }
}

function Coupons() {
  const { data } = useSuspenseQuery(couponsQuery);
  const [editing, setEditing] = useState<Coupon | "new" | null>(null);

  return (
    <>
      <Crumbs trail={[{ label: "Coupons" }]} />
      <Container className="divide-y p-0">
        <SectionHeader
          level="h1"
          title="Coupons"
          description="Codes you hand out: a percentage or amount off the next Quote Fees, or Credits. Each workspace can redeem a code once."
          actions={
            <Button size="small" onClick={() => setEditing("new")}>
              <Plus /> New coupon
            </Button>
          }
        />

        {data.coupons.length === 0 ? (
          <EmptyState
            icon={<ReceiptPercent />}
            title="No coupons yet"
            description="Create one to give a prospect a discount or a few Credits."
          />
        ) : (
          <Table>
            <Table.Header>
              <Table.Row>
                <Table.HeaderCell>Code</Table.HeaderCell>
                <Table.HeaderCell>Worth</Table.HeaderCell>
                <Table.HeaderCell>For</Table.HeaderCell>
                <Table.HeaderCell className="text-right">Redeemed</Table.HeaderCell>
                <Table.HeaderCell>Expires</Table.HeaderCell>
                <Table.HeaderCell>Status</Table.HeaderCell>
              </Table.Row>
            </Table.Header>
            <Table.Body>
              {data.coupons.map((coupon) => (
                <Table.Row key={coupon.id} data-clickable="true" onClick={() => setEditing(coupon)}>
                  <Table.Cell>
                    <div className="flex items-center gap-x-1.5">
                      <span className="code-label text-ui-fg-base">{coupon.code}</span>
                      <IconButton
                        size="small"
                        variant="transparent"
                        aria-label={`Copy ${coupon.code}`}
                        onClick={(event) => {
                          event.stopPropagation();
                          void copyCode(coupon.code);
                        }}
                      >
                        <SquareTwoStack />
                      </IconButton>
                    </div>
                    {coupon.note ? (
                      <div className="txt-compact-xsmall max-w-xs truncate text-ui-fg-muted">
                        {coupon.note}
                      </div>
                    ) : null}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    <div className="text-ui-fg-base">{worth(coupon)}</div>
                    <div className="txt-compact-xsmall text-ui-fg-muted">
                      {kindLabels[coupon.kind]}
                    </div>
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {coupon.workspaceKind === "yard"
                      ? "Yards only"
                      : coupon.workspaceKind === "builder"
                        ? "Builders only"
                        : "Any workspace"}
                  </Table.Cell>
                  <Table.Cell className="tabular whitespace-nowrap text-right">
                    {coupon.redemptions}
                    {coupon.maxRedemptions !== null ? ` of ${coupon.maxRedemptions}` : ""}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {coupon.expiresAt ? date(coupon.expiresAt) : "Never"}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap">
                    {!coupon.active ? (
                      <Badge color="grey">Inactive</Badge>
                    ) : expired(coupon) ? (
                      <Badge color="orange">Expired</Badge>
                    ) : coupon.maxRedemptions !== null &&
                      coupon.redemptions >= coupon.maxRedemptions ? (
                      <Badge color="orange">Used up</Badge>
                    ) : (
                      <Badge color="green">Active</Badge>
                    )}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        )}
      </Container>

      <Drawer open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        <Drawer.Content>
          {editing !== null ? (
            <CouponForm
              key={editing === "new" ? "new" : editing.id}
              existing={editing === "new" ? null : editing}
              onClose={() => setEditing(null)}
            />
          ) : null}
        </Drawer.Content>
      </Drawer>
    </>
  );
}

/** Creates or edits one coupon. Keyed by its id so state starts fresh each time. */
function CouponForm({ existing, onClose }: { existing: Coupon | null; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<Kind>(existing?.kind ?? "percent_off");
  const [workspaceKind, setWorkspaceKind] = useState(existing?.workspaceKind ?? "any");
  const [active, setActive] = useState(existing?.active ?? true);
  const [deleting, setDeleting] = useState(false);
  const { data: redemptions } = useQuery({
    ...couponRedemptionsQuery(existing?.id ?? ""),
    enabled: existing !== null,
  });

  const done = async (message: string) => {
    await queryClient.invalidateQueries({ queryKey: ["admin", "coupons"] });
    toast.success(message);
    onClose();
  };

  const save = useMutation({
    mutationFn: (form: FormData) => {
      const text = (name: string) => String(form.get(name) ?? "").trim();
      const expires = text("expiresAt");
      const json = {
        code: text("code"),
        kind,
        percentOff: kind === "percent_off" ? wholeNumber(form.get("percentOff")) : null,
        amountOffCents: kind === "amount_off" ? dollarsToCents(form.get("amountOff")) : null,
        freeQuotes: kind === "free_quotes" ? wholeNumber(form.get("freeQuotes")) : null,
        uses: kind === "free_quotes" ? 1 : Math.max(1, wholeNumber(form.get("uses"), 1)),
        maxRedemptions: text("maxRedemptions") ? wholeNumber(form.get("maxRedemptions")) : null,
        workspaceKind: workspaceKind === "any" ? null : (workspaceKind as "yard" | "builder"),
        // The last moment of that day, so a coupon "until the 30th" works all day on the 30th.
        expiresAt: expires ? new Date(`${expires}T23:59:59`).toISOString() : null,
        active,
        note: text("note") || null,
      };
      return existing
        ? unwrap(api.admin.coupons[":id"].$patch({ param: { id: existing.id }, json }))
        : unwrap(api.admin.coupons.$post({ json }));
    },
    onSuccess: ({ coupon }) => done(existing ? `${coupon.code} saved` : `${coupon.code} created`),
    onError: (error) => toast.error(error.message),
  });

  const remove = useMutation({
    mutationFn: () => unwrap(api.admin.coupons[":id"].$delete({ param: { id: existing!.id } })),
    onSuccess: () => done(`${existing!.code} deleted`),
    onError: (error) => toast.error(error.message),
  });

  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    save.mutate(new FormData(event.currentTarget));
  }

  const expiresDefault = existing?.expiresAt
    ? new Date(existing.expiresAt).toISOString().slice(0, 10)
    : "";

  return (
    <form onSubmit={submit} className="flex min-h-0 flex-1 flex-col">
      <Drawer.Header>
        <Drawer.Title>{existing ? existing.code : "New coupon"}</Drawer.Title>
        <Drawer.Description>
          {existing
            ? `Redeemed ${existing.redemptions} ${existing.redemptions === 1 ? "time" : "times"}. Changes apply to future redemptions only.`
            : "Give it a short code people can type. It is stored in upper case."}
        </Drawer.Description>
      </Drawer.Header>

      <Drawer.Body className="flex flex-col gap-y-5">
        <Field label="Code" hint="Letters, digits and hyphens.">
          {(control) => (
            <Input
              {...control}
              name="code"
              required
              pattern="[A-Za-z0-9-]{3,40}"
              title="Letters, digits and hyphens, 3 to 40 characters."
              autoCapitalize="characters"
              autoCorrect="off"
              spellCheck={false}
              autoFocus={!existing}
              placeholder="WELCOME50"
              defaultValue={existing?.code ?? ""}
              className="uppercase"
            />
          )}
        </Field>

        <Field label="What it gives">
          {(control) => (
            <Select value={kind} onValueChange={(value) => setKind(value as Kind)}>
              <Select.Trigger id={control.id} aria-describedby={control["aria-describedby"]}>
                <Select.Value />
              </Select.Trigger>
              <Select.Content>
                {(Object.keys(kindLabels) as Kind[]).map((value) => (
                  <Select.Item key={value} value={value}>
                    {kindLabels[value]}
                  </Select.Item>
                ))}
              </Select.Content>
            </Select>
          )}
        </Field>

        <div className="grid grid-cols-2 gap-4">
          {kind === "percent_off" ? (
            <Field label="Percent off">
              {(control) => (
                <Input
                  {...control}
                  name="percentOff"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={100}
                  step={1}
                  required
                  defaultValue={existing?.percentOff ?? 50}
                />
              )}
            </Field>
          ) : null}
          {kind === "amount_off" ? (
            <Field label="Amount off">
              {(control) => (
                <Input
                  {...control}
                  name="amountOff"
                  inputMode="decimal"
                  prefix="$"
                  required
                  pattern="[0-9]*\.?[0-9]{0,2}"
                  defaultValue={centsToDollars(existing?.amountOffCents ?? 5000)}
                />
              )}
            </Field>
          ) : null}
          {kind === "free_quotes" ? (
            <Field
              label="Credits"
              hint="Each covers one released takeoff, added the moment the coupon is redeemed."
            >
              {(control) => (
                <Input
                  {...control}
                  name="freeQuotes"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={1000}
                  step={1}
                  required
                  defaultValue={existing?.freeQuotes ?? 1}
                />
              )}
            </Field>
          ) : (
            <Field label="Quote fees it applies to" hint="After one redemption.">
              {(control) => (
                <Input
                  {...control}
                  name="uses"
                  type="number"
                  inputMode="numeric"
                  min={1}
                  max={1000}
                  step={1}
                  required
                  defaultValue={existing?.uses ?? 1}
                />
              )}
            </Field>
          )}
          <Field
            label="Redemptions allowed"
            optional
            hint="Across all workspaces. Empty means unlimited."
          >
            {(control) => (
              <Input
                {...control}
                name="maxRedemptions"
                type="number"
                inputMode="numeric"
                min={1}
                step={1}
                defaultValue={existing?.maxRedemptions ?? ""}
              />
            )}
          </Field>
          <Field label="Who may redeem it">
            {(control) => (
              <Select value={workspaceKind} onValueChange={setWorkspaceKind}>
                <Select.Trigger id={control.id} aria-describedby={control["aria-describedby"]}>
                  <Select.Value />
                </Select.Trigger>
                <Select.Content>
                  <Select.Item value="any">Any workspace</Select.Item>
                  <Select.Item value="yard">Yards only</Select.Item>
                  <Select.Item value="builder">Builders only</Select.Item>
                </Select.Content>
              </Select>
            )}
          </Field>
          <Field label="Expires" optional hint="Usable through the end of that day.">
            {(control) => (
              <Input {...control} name="expiresAt" type="date" defaultValue={expiresDefault} />
            )}
          </Field>
        </div>

        <Field label="Note" optional hint="Who it is for, or the campaign. Never shown to Members.">
          {(control) => (
            <Textarea
              {...control}
              name="note"
              rows={2}
              maxLength={400}
              defaultValue={existing?.note ?? ""}
            />
          )}
        </Field>

        <ul className="divide-y divide-ui-border-base rounded-lg shadow-borders-base">
          <li className="flex items-center justify-between gap-x-4 px-4 py-3">
            <label htmlFor="coupon-active" className="min-w-0 cursor-pointer">
              <span className="txt-compact-small-plus block text-ui-fg-base">Active</span>
              <span className="txt-compact-xsmall block text-ui-fg-muted">
                An inactive coupon cannot be redeemed. Discounts already applied stay in force.
              </span>
            </label>
            <Switch id="coupon-active" checked={active} onCheckedChange={setActive} />
          </li>
        </ul>

        {existing ? (
          <div className="flex flex-col gap-y-2">
            <span className="txt-compact-small-plus text-ui-fg-base">Redemptions</span>
            {!redemptions || redemptions.redemptions.length === 0 ? (
              <span className="txt-small text-ui-fg-muted">
                Nobody has redeemed this coupon yet.
              </span>
            ) : (
              <ul className="divide-y divide-ui-border-base rounded-lg shadow-borders-base">
                {redemptions.redemptions.map((item) => (
                  <li
                    key={item.id}
                    className="flex items-center justify-between gap-x-4 px-4 py-2.5"
                  >
                    <span className="min-w-0">
                      <span className="txt-compact-small block truncate text-ui-fg-base">
                        {item.yardName}
                      </span>
                      <span className="txt-compact-xsmall block truncate text-ui-fg-muted">
                        {item.userEmail ?? ""}
                      </span>
                    </span>
                    <span className="txt-compact-xsmall shrink-0 text-ui-fg-muted">
                      {date(item.createdAt)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}
      </Drawer.Body>

      <Drawer.Footer>
        {existing && existing.redemptions === 0 ? (
          <Button
            type="button"
            variant="danger"
            size="small"
            className="mr-auto"
            onClick={() => setDeleting(true)}
          >
            Delete
          </Button>
        ) : null}
        <Button type="button" variant="secondary" size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit" size="small" isLoading={save.isPending}>
          {existing ? "Save" : "Create coupon"}
        </Button>
      </Drawer.Footer>

      <Prompt open={deleting} onOpenChange={setDeleting}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Delete {existing?.code}?</Prompt.Title>
            <Prompt.Description>Nobody has used it, so nothing else changes.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Keep it</Prompt.Cancel>
            <Button
              size="small"
              variant="danger"
              isLoading={remove.isPending}
              onClick={() => remove.mutate()}
            >
              Delete
            </Button>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </form>
  );
}
